/**
 * Which grid columns are hidden.
 *
 * Hiding is a *view* concern only: the result set still holds every column, so
 * nothing that addresses a cell by its index into result.columns — pending
 * edits, the WHERE builder, export, FK navigation — changes meaning when a
 * column is hidden. The grid skips drawing hidden columns rather than
 * filtering the arrays, which is what keeps those indexes true.
 *
 * Stored on the tab as an array (tabs are serialised into the workspace
 * snapshot, and a Set is not JSON).
 */

/** Hidden columns as a Set, tolerating the absent/legacy shapes on a tab. */
export function hiddenSet(hidden) {
  return new Set(Array.isArray(hidden) ? hidden.filter((c) => typeof c === 'string') : []);
}

/**
 * Hidden columns after toggling `col`, or `null` if the toggle is refused.
 *
 * The one refusal is hiding the last visible column: an empty grid looks
 * broken rather than filtered, and there would be no header left to right-click
 * to undo it.
 */
export function toggleColumn(hidden, col, allColumns) {
  const set = hiddenSet(hidden);
  if (set.has(col)) {
    set.delete(col);
    return [...set];
  }
  if (!Array.isArray(allColumns) || !allColumns.includes(col)) return null;
  const wouldRemain = allColumns.filter((c) => c !== col && !set.has(c));
  if (wouldRemain.length === 0) return null;
  set.add(col);
  return [...set];
}

/**
 * Hidden columns after setting an explicit selection of visible ones. Same
 * last-column rule; an empty selection is refused rather than silently
 * corrected, so the caller can say why.
 */
export function setVisibleColumns(allColumns, visible) {
  if (!Array.isArray(allColumns)) return null;
  const keep = new Set(Array.isArray(visible) ? visible : []);
  const remaining = allColumns.filter((c) => keep.has(c));
  if (remaining.length === 0) return null;
  return allColumns.filter((c) => !keep.has(c));
}

/** Columns still drawn, in their original order. */
export function visibleColumns(allColumns, hidden) {
  const set = hiddenSet(hidden);
  return (Array.isArray(allColumns) ? allColumns : []).filter((c) => !set.has(c));
}

/**
 * Hidden entries that no longer name a column in this result.
 *
 * A tab keeps its hidden list across re-queries, and a changed projection (a
 * different SELECT, an ALTER TABLE) can leave names behind that match nothing.
 * They are harmless to the grid but would make "3 hidden" count columns that
 * do not exist, so the badge counts only the live ones.
 */
export function liveHidden(allColumns, hidden) {
  const cols = new Set(Array.isArray(allColumns) ? allColumns : []);
  return [...hiddenSet(hidden)].filter((c) => cols.has(c));
}
